import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DropdownProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// Componente Dropdown neobrutalist per react-day-picker
function BrutalCalendarDropdown(props: DropdownProps) {
  const { caption, children, className, value, onChange, name, "aria-label": ariaLabel } = props;
  
  const handleValueChange = (newValue: string) => {
    if (onChange) {
      const syntheticEvent = {
        target: { value: newValue },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    }
  };

  // Estrai le opzioni dai children (option elements)
  const options: { value: string; label: string; disabled?: boolean }[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === 'option') {
      const optionProps = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
      options.push({
        value: String(optionProps.value ?? ''),
        label: String(optionProps.children ?? ''),
        disabled: optionProps.disabled,
      });
    }
  });

  const selectedOption = options.find((o) => o.value === String(value));

  return (
    <BrutalSelect value={String(value ?? '')} onValueChange={handleValueChange}>
      <BrutalSelectTrigger 
        className={cn("h-8 px-2 py-1 text-sm border-2 min-w-[80px]", className)}
        aria-label={ariaLabel}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <BrutalSelectValue>
          {selectedOption?.label ?? caption}
        </BrutalSelectValue>
      </BrutalSelectTrigger>
      <BrutalSelectContent className="z-[200] max-h-60 overflow-y-auto">
        {options.map((option) => (
          <BrutalSelectItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className="text-sm py-1"
          >
            {option.label}
          </BrutalSelectItem>
        ))}
      </BrutalSelectContent>
    </BrutalSelect>
  );
}

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 pointer-events-auto", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center gap-1",
        caption_label: "hidden",
        caption_dropdowns: "flex items-center gap-1",
        dropdown_month: "",
        dropdown_year: "",
        dropdown: "hidden",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
        Dropdown: BrutalCalendarDropdown,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
